import { useEffect, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";

import Modal from "newui/components/ui/Modal/Modal";
import {
	InputWithLabel,
	SelectWithLabel,
} from "newui/components/ui/InputWithLabel";
import type { SelectOption } from "newui/components/ui/InputWithLabel";
import { showToast } from "utils/helpers";

import {
	APPROVER_EVENT_ROLE_OPTIONS,
	PANEL_EVENT_ROLE_OPTIONS,
	RBAC_STRINGS,
} from "../../constants/rbacStrings";
import {
	useInviteApprover,
	useInvitePanelMember,
} from "../../hooks/useEventGrantQueries";
import { useGuestActionBlock } from "../../hooks/useGuestActionBlock";
import {
	isCoiBlocked,
	parseRbacApiError,
} from "../../utils/rbacApiError";
import type {
	EventGrantKind,
	EventRole,
} from "../../types/eventGrant.type";
import { GuestUpgradeCta } from "./GuestUpgradeCta";

/**
 * Invite a principal to an RFx event as a panel member or approver (§5.1).
 * The role dropdown selects the `event_role` (§3 amendment — NOT `side`).
 *
 * COI is server-enforced (hard rule 1): a conflicted invitee is BLOCKED by the
 * backend (409 `rbac.coi_blocked`); the modal surfaces the server verdict
 * inline, stays open, and does NOT send the invite (AC1). Guests are view-only
 * (FE-4): the submit control is disabled and an upgrade CTA is shown.
 */

interface PanelInviteModalProps {
	isOpen: boolean;
	rfxId: number;
	evalId: number;
	/** Panel-side (default) or approver-side invite; selects endpoint + roles. */
	kind?: EventGrantKind;
	/** Pins an unregistered invitee to their real company (007 finding 2b). */
	inviteeCompanyId?: number;
	onClose: () => void;
	onInvited?: () => void;
	/** Starts the guest company-upgrade flow (VT-027). */
	onUpgrade?: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const PanelInviteModal = ({
	isOpen,
	rfxId,
	evalId,
	kind = "panel",
	inviteeCompanyId,
	onClose,
	onInvited,
	onUpgrade,
}: PanelInviteModalProps) => {
	const isApprover = kind === "approver";
	const roleOptions = (
		isApprover ? APPROVER_EVENT_ROLE_OPTIONS : PANEL_EVENT_ROLE_OPTIONS
	) as ReadonlyArray<SelectOption>;

	const { isGuest } = useGuestActionBlock();

	const panelMutation = useInvitePanelMember(rfxId, evalId);
	const approverMutation = useInviteApprover(rfxId, evalId);
	const mutation = isApprover ? approverMutation : panelMutation;

	const [email, setEmail] = useState("");
	const [role, setRole] = useState<EventRole | null>(null);
	const [conflictMessage, setConflictMessage] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	useEffect(() => {
		if (isOpen) {
			setEmail("");
			setRole(null);
			setConflictMessage(null);
			setErrorMessage(null);
		}
	}, [isOpen]);

	const emailValid = EMAIL_PATTERN.test(email.trim());
	const canSubmit =
		!isGuest && emailValid && role !== null && !mutation.isPending;

	const handleSubmit = (): void => {
		if (!canSubmit || role === null) {
			return;
		}
		setConflictMessage(null);
		setErrorMessage(null);
		mutation.mutate(
			{
				email: email.trim(),
				event_role: role,
				...(inviteeCompanyId ? { company_id: inviteeCompanyId } : {}),
			},
			{
				onSuccess: () => {
					showToast(RBAC_STRINGS.invite.success, "Invite sent", true);
					onInvited?.();
					onClose();
				},
				onError: (err) => {
					const parsed = parseRbacApiError(err);
					// COI hard rule 1: surface the server verdict, keep the modal
					// open, invite NOT sent (AC1).
					if (isCoiBlocked(parsed)) {
						setConflictMessage(
							parsed.message ?? RBAC_STRINGS.invite.coiBlocked
						);
						return;
					}
					setErrorMessage(
						parsed.message ?? RBAC_STRINGS.invite.genericError
					);
				},
			}
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={RBAC_STRINGS.invite.title}
			size="md"
			iconType="mail"
			modalType="standard"
			closeOnEsc
			closeOnOverlayClick={false}
			primaryButton={{
				text: RBAC_STRINGS.invite.submit,
				onClick: handleSubmit,
				width: "full",
				disabled: !canSubmit,
				isLoading: mutation.isPending,
			}}
			secondaryButton={{
				text: RBAC_STRINGS.invite.cancel,
				onClick: onClose,
				width: "full",
			}}
		>
			<div className="space-y-4" data-testid="panel-invite-modal">
				<InputWithLabel
					label={RBAC_STRINGS.invite.emailLabel}
					required
					type="email"
					value={email}
					placeholder={RBAC_STRINGS.invite.emailPlaceholder}
					onChange={(e) => setEmail(e.target.value)}
					disabled={isGuest}
					testId="panel-invite-email"
					hasError={email.length > 0 && !emailValid}
					errorText={
						email.length > 0 && !emailValid
							? "Enter a valid email address."
							: undefined
					}
				/>

				<SelectWithLabel
					label={RBAC_STRINGS.invite.roleLabel}
					required
					options={roleOptions as SelectOption[]}
					placeholder={RBAC_STRINGS.invite.rolePlaceholder}
					disabled={isGuest}
					testId="panel-invite-role"
					value={
						roleOptions.find((option) => option.value === role) ?? null
					}
					onChange={(option) => {
						const selected = option as SelectOption | null;
						setRole((selected?.value as EventRole) ?? null);
					}}
				/>

				{conflictMessage && (
					<div
						className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
						role="alert"
						data-testid="panel-invite-coi-block"
					>
						<FiAlertTriangle
							size={16}
							className="mt-0.5 shrink-0 text-red-600"
							aria-hidden="true"
						/>
						<p className="text-sm text-red-700">{conflictMessage}</p>
					</div>
				)}

				{errorMessage && (
					<p
						className="text-sm text-red-600"
						role="alert"
						data-testid="panel-invite-error"
					>
						{errorMessage}
					</p>
				)}

				{isGuest && <GuestUpgradeCta onUpgrade={onUpgrade} />}
			</div>
		</Modal>
	);
};
